import { isApiError } from '@missions/api-client';

/* =============================================================================
   Cold start (Render free tier): the API sleeps when idle and the first request
   can take 30–60s. Boot requests retry with backoff for up to ~70s while a
   friendly "Waking up…" radar screen is shown, instead of an error.
   ========================================================================== */

/** Network failure or gateway/5xx while the instance spins up. */
export const isWaking = (e: unknown): boolean =>
  !isApiError(e) || e.status === 0 || e.status === 502 || e.status === 503 || e.status === 504 || e.code === 'service_unavailable';

export async function withWakeRetry<T>(fn: () => Promise<T>, onWaiting: (elapsedMs: number) => void, budgetMs = 70_000): Promise<T> {
  const start = Date.now();
  let delay = 1500;
  for (;;) {
    try {
      return await fn();
    } catch (e) {
      const elapsed = Date.now() - start;
      if (!isWaking(e) || elapsed + delay > budgetMs) throw e;
      onWaiting(elapsed);
      await new Promise((r) => setTimeout(r, delay));
      delay = Math.min(delay * 1.6, 10_000);
    }
  }
}

/** Shared offer links paint the hero navy from the first frame (index.html sets html.offer-boot too). */
export const isOfferPath = (path = window.location.pathname): boolean => /^\/(offer|claim)(\/|$)/.test(path);

/** Offer-link boot: full-bleed hero navy, no white card, so the landing page fades in on the same colour. */
function OfferBoot({ waking, failed, onRetry }: { waking?: boolean; failed?: boolean; onRetry?: () => void }) {
  return (
    <div className="boot-offer" role="status" aria-live="polite"
      style={{ position: 'fixed', inset: 0, display: 'grid', placeItems: 'center', alignContent: 'center', gap: 14, padding: 24, textAlign: 'center',
        background: 'linear-gradient(160deg, #0b1a4a, #0437b8)', color: '#c7d6ff', fontWeight: 600, fontSize: '0.875rem' }}>
      <span className="ms-radar" style={{ ['--s' as string]: '56px', opacity: 0.85 }} aria-hidden="true">
        <span className="blip" style={{ left: '68%', top: '30%' }} />
      </span>
      {failed ? (
        <>
          <b style={{ color: '#fff', fontSize: '1rem' }}>We couldn’t reach the server</b>
          <button type="button" className="ui-btn primary" onClick={onRetry}>Try again</button>
        </>
      ) : waking ? (
        <b style={{ color: '#fff', fontSize: '1rem' }}>Opening your invitation…</b>
      ) : (
        <span className="sr">Loading…</span>
      )}
    </div>
  );
}

/** Static boot screens (rendered before providers exist). */
export function BootScreen({ waking, failed, onRetry }: { waking?: boolean; failed?: boolean; onRetry?: () => void }) {
  if (isOfferPath()) return <OfferBoot waking={waking} failed={failed} onRetry={onRetry} />;
  return (
    <div className="boot-load" role="status" aria-live="polite">
      <span className="ms-radar" style={{ ['--s' as string]: '64px' }} aria-hidden="true">
        <span className="blip" style={{ left: '68%', top: '30%' }} />
        <span className="blip" style={{ left: '28%', top: '62%', animationDelay: '.9s' }} />
      </span>
      {failed ? (
        <>
          <b style={{ color: 'var(--text)', fontSize: '1rem' }}>We couldn’t reach the server</b>
          <span>Check your connection, then try again.</span>
          <button type="button" className="ui-btn primary" onClick={onRetry}>Try again</button>
        </>
      ) : waking ? (
        <>
          <b style={{ color: 'var(--text)', fontSize: '1rem' }}>Waking up…</b>
          <span>Your workspace was resting. This can take up to a minute the first time.</span>
        </>
      ) : (
        <span>Loading…</span>
      )}
    </div>
  );
}
